import React from 'react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

// Sample preview image URL for filter demonstrations
const PREVIEW_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=100&h=100&fit=crop';

interface FiltersPanelProps {
  selectedFilter: string;
  onSelectFilter: (filterId: string) => void;
  previewImage?: string;
}

const FiltersPanel: React.FC<FiltersPanelProps> = ({
  selectedFilter,
  onSelectFilter,
  previewImage,
}) => {
  const { t } = useLanguage();
  const imageToUse = previewImage || PREVIEW_IMAGE;

  const FILTERS = [
    { id: 'none', nameKey: 'filterOriginal' as const, filter: '' },
    { id: '1977', nameKey: 'filter1977' as const, filter: 'sepia(0.3) contrast(1.1) brightness(1.1) saturate(1.3)' },
    { id: 'brannan', nameKey: 'filterBrannan' as const, filter: 'sepia(0.5) contrast(1.4)' },
    { id: 'gotham', nameKey: 'filterGotham' as const, filter: 'grayscale(1) contrast(1.2) brightness(0.9)' },
    { id: 'hefe', nameKey: 'filterHefe' as const, filter: 'saturate(1.4) contrast(1.1) brightness(1.1) sepia(0.15)' },
    { id: 'graymouse', nameKey: 'filterGraymouse' as const, filter: 'grayscale(0.5) contrast(1.1)' },
    { id: 'kelvin', nameKey: 'filterKelvin' as const, filter: 'sepia(0.3) saturate(1.6) brightness(1.1)' },
    { id: 'nashville', nameKey: 'filterNashville' as const, filter: 'sepia(0.2) contrast(1.2) brightness(1.05) saturate(1.2)' },
    { id: 'xpro2', nameKey: 'filterXpro2' as const, filter: 'sepia(0.3) contrast(1.3) saturate(1.4)' },
    { id: 'azure', nameKey: 'filterAzure' as const, filter: 'saturate(1.5) hue-rotate(180deg) brightness(1.1)' },
    { id: 'mist', nameKey: 'filterMist' as const, filter: 'brightness(0.9) contrast(0.9) saturate(0.8)' },
    { id: 'purple', nameKey: 'filterPurple' as const, filter: 'hue-rotate(270deg) saturate(1.2)' },
    { id: 'nature', nameKey: 'filterNature' as const, filter: 'saturate(1.3) brightness(1.05) contrast(1.05)' },
    { id: 'vintage', nameKey: 'filterVintage' as const, filter: 'sepia(0.4) saturate(0.8) brightness(1.1)' },
    { id: 'warm', nameKey: 'filterWarm' as const, filter: 'sepia(0.2) saturate(1.3) brightness(1.05)' },
    { id: 'cool', nameKey: 'filterCool' as const, filter: 'saturate(0.9) brightness(1.1) hue-rotate(10deg)' },
    { id: 'sunrise', nameKey: 'filterSunrise' as const, filter: 'saturate(1.4) brightness(1.15) sepia(0.15)' },
    { id: 'noir', nameKey: 'filterNoir' as const, filter: 'grayscale(1) contrast(1.4) brightness(0.85)' },
  ];

  return (
    <div className="space-y-3">
      <h2 className="panel-title font-semibold text-foreground">{t.filtersTitle}</h2>
      <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
        {FILTERS.map((filter) => (
          <button
            key={filter.id}
            onClick={() => onSelectFilter(filter.id)}
            aria-label={`Filter ${t[filter.nameKey]}`}
            className={cn(
              "flex flex-col items-center gap-1 p-1.5 rounded-lg transition-all duration-200",
              "border hover:border-primary/50",
              selectedFilter === filter.id
                ? "border-primary bg-primary/20 ring-1 ring-primary/30"
                : "border-border bg-card/50 hover:bg-card"
            )}
          >
            <div 
              className="w-full aspect-square rounded-md overflow-hidden bg-muted"
              style={{ filter: filter.filter }}
            >
              <img 
                src={imageToUse} 
                alt={t[filter.nameKey]}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <span className={cn(
              "panel-label font-medium truncate w-full text-center",
              selectedFilter === filter.id ? "text-foreground" : "text-muted-foreground"
            )}>
              {t[filter.nameKey]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default FiltersPanel;

export const FILTERS = [
  { id: 'none', filter: '' },
  { id: '1977', filter: 'sepia(0.3) contrast(1.1) brightness(1.1) saturate(1.3)' },
  { id: 'brannan', filter: 'sepia(0.5) contrast(1.4)' },
  { id: 'gotham', filter: 'grayscale(1) contrast(1.2) brightness(0.9)' },
  { id: 'hefe', filter: 'saturate(1.4) contrast(1.1) brightness(1.1) sepia(0.15)' },
  { id: 'graymouse', filter: 'grayscale(0.5) contrast(1.1)' },
  { id: 'kelvin', filter: 'sepia(0.3) saturate(1.6) brightness(1.1)' },
  { id: 'nashville', filter: 'sepia(0.2) contrast(1.2) brightness(1.05) saturate(1.2)' },
  { id: 'xpro2', filter: 'sepia(0.3) contrast(1.3) saturate(1.4)' },
  { id: 'azure', filter: 'saturate(1.5) hue-rotate(180deg) brightness(1.1)' },
  { id: 'mist', filter: 'brightness(0.9) contrast(0.9) saturate(0.8)' },
  { id: 'purple', filter: 'hue-rotate(270deg) saturate(1.2)' },
  { id: 'nature', filter: 'saturate(1.3) brightness(1.05) contrast(1.05)' },
  { id: 'vintage', filter: 'sepia(0.4) saturate(0.8) brightness(1.1)' },
  { id: 'warm', filter: 'sepia(0.2) saturate(1.3) brightness(1.05)' },
  { id: 'cool', filter: 'saturate(0.9) brightness(1.1) hue-rotate(10deg)' },
  { id: 'sunrise', filter: 'saturate(1.4) brightness(1.15) sepia(0.15)' },
  { id: 'noir', filter: 'grayscale(1) contrast(1.4) brightness(0.85)' },
];
